import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import "bootstrap/dist/css/bootstrap.min.css";
import { useAppServices } from "../../../../hook/services";
import { useKnowledgebaseCustomizationInfo } from "../../../../context/knowledgebaseCustomization";
import { toast } from "react-toastify";

const CategoryArticles = () => {
  const { categoryId, companyId } = useParams();
  const navigate = useNavigate();
  const AppService = useAppServices();
  const [KnowledgebaseCustomization] = useKnowledgebaseCustomizationInfo();
  const backUrl = `/${companyId}/knowledgebase`;

  const [category, setCategory] = useState(null);
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");

  const getCategory = async () => {
    try {
      const { response } = await AppService.knowledgebase_category.filter({
        query: `_id=${categoryId}`,
      });
      if (response?.data) {
        setCategory(response.data);
      } else {
        setCategory(null);
      }
    } catch (error) {
      console.error(error);
      setCategory(null);
    } finally {
    }
  };

  const getArticles = async () => {
    setLoading(true);
    try {
      const { response } = await AppService.knowledgebase_article.get_agency_articles({
        query: `companyId=${companyId}&category=${categoryId}`,
      });
      if (response) {
        setArticles(response.data);
      } else {
        setArticles([]);
      }
    } catch (error) {
      console.error(error);
      setArticles([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getCategory();
    getArticles();
  }, [categoryId]);

  const handleDelete = async (id) => {
    const { response } = await AppService?.knowledgebase_article.delete({
      query: `_id=${id}`,
    });

    if (response) {
      getArticles();
      toast.success("Article deleted!");
    } else {
      toast.error(`Error deleting Article`);
    }
  };

  const handleVisibility = async (data, status) => {
    const { response } =
      await AppService.knowledgebase_category.data_visibility({
        payload: {
          companyId: companyId,
          dataId: data?._id,
          dataType: "ARTICLE",
          status: status,
        },
      });
    if (response) {
      getArticles();
    }
  };

  if (loading) {
    return (
      <div className="kb-page d-flex flex-column align-items-center justify-content-center py-5 text-secondary" style={{ minHeight: "60vh" }}>
        <div className="spinner-border spinner-border-sm mb-2" role="status" aria-hidden="true" />
        <span style={{ fontSize: "0.875rem" }}>Loading articles…</span>
        <KbStyles />
      </div>
    );
  }

  if (!category) {
    return (
      <div className="kb-page">
        <div className="container py-5 text-center">
          <div className="kb-empty d-flex flex-column align-items-center mx-auto" style={{ maxWidth: 420 }}>
            <div className="kb-empty-icon d-flex align-items-center justify-content-center mb-3">
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
                <path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z" />
                <line x1="2" y1="2" x2="22" y2="22" />
              </svg>
            </div>
            <h5 className="kb-empty-title">Category not found</h5>
            <p className="kb-empty-text mb-3">
              The category you're looking for doesn't exist or may have been removed.
            </p>
            <Link to={backUrl} className="kb-btn kb-btn--ghost">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="me-1">
                <polyline points="15 18 9 12 15 6" />
              </svg>
              Go back
            </Link>
          </div>
        </div>
        <KbStyles />
      </div>
    );
  }

  const normalizedQuery = searchQuery.trim().toLowerCase();
  const filteredArticles = !normalizedQuery
    ? articles
    : articles.filter((a) => {
        const name = (a.name || "").toLowerCase();
        const desc = (a.shortDescription || "").toLowerCase();
        return name.includes(normalizedQuery) || desc.includes(normalizedQuery);
      });

  const headerTextColor = KnowledgebaseCustomization?.textColor || "white";

  return (
    <div className="kb-page">
      {/* Hero */}
      <header
        className="kb-hero kb-hero--with-back"
        style={{ background: KnowledgebaseCustomization?.bgColor || "#6c757d" }}
      >
        <div className="container kb-hero-inner">
          <div className="d-flex justify-content-start mb-3">
            <Link to={backUrl} className="kb-hero-back" style={{ color: headerTextColor }}>
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="me-1">
                <polyline points="15 18 9 12 15 6" />
              </svg>
              <span style={{ fontSize: "0.875rem", fontWeight: 500 }}>Back</span>
            </Link>
          </div>

          <div className="text-center">
            <span className="kb-hero-icon d-inline-flex align-items-center justify-content-center mb-3" style={{ color: headerTextColor }}>
              <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                <path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z" />
              </svg>
            </span>
            <h1 className="kb-hero-title" style={{ color: headerTextColor }}>
              {category.name}
            </h1>
            {category.description && (
              <p className="kb-hero-subtitle" style={{ color: headerTextColor }}>
                {category.description}
              </p>
            )}
            <span className="kb-hero-badge">
              {normalizedQuery
                ? `${filteredArticles.length} of ${articles.length} articles`
                : `${articles.length} ${articles.length === 1 ? "article" : "articles"} available`}
            </span>
          </div>
        </div>
      </header>

      {/* Search + Create */}
      <div className="container kb-toolbar d-flex flex-column gap-3">
        <div className="kb-search-wrap position-relative mx-auto w-100" style={{ maxWidth: 560 }}>
          <span className="kb-search-icon d-flex align-items-center justify-content-center" aria-hidden>
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="11" cy="11" r="7" />
              <line x1="21" y1="21" x2="16.65" y2="16.65" />
            </svg>
          </span>
          <input
            type="text"
            className="kb-search-input"
            placeholder={`Search within ${category.name}…`}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
          {searchQuery && (
            <button
              className="kb-search-clear"
              type="button"
              onClick={() => setSearchQuery("")}
              title="Clear search"
              aria-label="Clear search"
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <line x1="18" y1="6" x2="6" y2="18" />
                <line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </button>
          )}
        </div>
        {normalizedQuery && (
          <div className="text-center kb-result-line">
            {filteredArticles.length === 0
              ? `No articles match "${searchQuery}"`
              : `Showing results for "${searchQuery}"`}
          </div>
        )}

        <div className="d-flex justify-content-end">
          <Link to={`../create_article/${category?._id}`} className="kb-btn kb-btn--primary">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" className="me-1">
              <line x1="12" y1="5" x2="12" y2="19" />
              <line x1="5" y1="12" x2="19" y2="12" />
            </svg>
            Create article
          </Link>
        </div>
      </div>

      {/* Article list */}
      <div className="container kb-section">
        <h4 className="kb-section-title-left">
          {normalizedQuery ? "Search results" : `All articles in ${category.name}`}
        </h4>

        {filteredArticles.length === 0 ? (
          <div className="kb-empty d-flex flex-column align-items-center text-center mx-auto" style={{ maxWidth: 480 }}>
            <div className="kb-empty-icon d-flex align-items-center justify-content-center mb-3">
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="11" cy="11" r="7" />
                <line x1="21" y1="21" x2="16.65" y2="16.65" />
              </svg>
            </div>
            <h6 className="kb-empty-title">
              {normalizedQuery ? "No matching articles" : "No articles found in this category"}
            </h6>
            {normalizedQuery && (
              <p className="kb-empty-text mb-0">
                Try a different search term or{" "}
                <button
                  type="button"
                  className="kb-link-btn"
                  onClick={() => setSearchQuery("")}
                >
                  clear the search
                </button>
                .
              </p>
            )}
          </div>
        ) : (
          <div className="d-flex flex-column gap-2">
            {filteredArticles.map((a) => (
              <article
                key={a._id}
                role="button"
                onClick={() => navigate(`./${a._id}`)}
                className="kb-article-card"
              >
                <div className="d-flex justify-content-between align-items-start gap-3 flex-wrap">
                  <div className="flex-grow-1" style={{ minWidth: 0 }}>
                    <h6 className="kb-article-card-title mb-1">{a.name}</h6>
                    {a.shortDescription && (
                      <p className="kb-article-card-desc mb-1">{a.shortDescription}</p>
                    )}
                    <div className="kb-article-card-meta d-inline-flex align-items-center">
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="me-1">
                        <circle cx="12" cy="12" r="10" />
                        <polyline points="12 6 12 12 16 14" />
                      </svg>
                      Updated {new Date(a.updatedAt).toLocaleDateString()}
                    </div>
                  </div>

                  <div className="d-flex gap-1 flex-shrink-0">
                    {a?.createdBy == "AGENCY" ? (
                      <>
                        <button
                          className="kb-icon-btn"
                          title="Edit"
                          onClick={(e) => {
                            e.stopPropagation();
                            navigate(`../update_article/${a._id}`);
                          }}
                        >
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M12 20h9" />
                            <path d="M16.5 3.5a2.121 2.121 0 1 1 3 3L7 19l-4 1 1-4 12.5-12.5z" />
                          </svg>
                        </button>
                        <button
                          className="kb-icon-btn kb-icon-btn--danger"
                          title="Delete"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDelete(a._id);
                          }}
                        >
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <polyline points="3 6 5 6 21 6" />
                            <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6" />
                            <path d="M10 11v6" />
                            <path d="M14 11v6" />
                          </svg>
                        </button>
                      </>
                    ) : a?.hidden ? (
                      <button
                        className="kb-icon-btn kb-icon-btn--active"
                        title="Show"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleVisibility(a, "visible");
                        }}
                      >
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94" />
                          <path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19" />
                          <path d="M14.12 14.12A3 3 0 1 1 9.88 9.88" />
                          <line x1="1" y1="1" x2="23" y2="23" />
                        </svg>
                      </button>
                    ) : (
                      <button
                        className="kb-icon-btn"
                        title="Hide"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleVisibility(a, "hide");
                        }}
                      >
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                          <circle cx="12" cy="12" r="3" />
                        </svg>
                      </button>
                    )}
                  </div>
                </div>
              </article>
            ))}
          </div>
        )}
      </div>

      <KbStyles />
    </div>
  );
};

const KbStyles = () => (
  <style>{`
    .kb-page {
      font-family: 'Inter', sans-serif;
      background: #fff;
      min-height: 100vh;
    }

    /* Hero */
    .kb-hero {
      padding: 28px 16px 56px;
    }
    .kb-hero--with-back { padding-top: 18px; }
    .kb-hero-inner { max-width: 760px; margin: 0 auto; }
    .kb-hero-back {
      display: inline-flex;
      align-items: center;
      padding: 6px 10px;
      border-radius: 8px;
      text-decoration: none;
      background: rgba(255, 255, 255, 0.16);
      border: 1px solid rgba(255, 255, 255, 0.18);
      transition: background 0.15s ease, transform 0.15s ease;
    }
    .kb-hero-back:hover {
      background: rgba(255, 255, 255, 0.26);
      text-decoration: none;
    }
    .kb-hero-back:hover svg { transform: translateX(-2px); }
    .kb-hero-back svg { transition: transform 0.15s ease; }
    .kb-hero-icon {
      width: 56px;
      height: 56px;
      border-radius: 16px;
      background: rgba(255, 255, 255, 0.16);
      border: 1px solid rgba(255, 255, 255, 0.2);
    }
    .kb-hero-title {
      font-size: 1.75rem;
      font-weight: 700;
      letter-spacing: -0.01em;
      margin: 0 0 8px;
    }
    .kb-hero-subtitle {
      font-size: 0.9375rem;
      opacity: 0.92;
      margin: 0 0 16px;
    }
    .kb-hero-badge {
      display: inline-flex;
      align-items: center;
      padding: 5px 12px;
      font-size: 0.75rem;
      font-weight: 500;
      color: #1f2937;
      background: rgba(255, 255, 255, 0.92);
      border-radius: 999px;
      letter-spacing: 0.02em;
    }

    /* Toolbar */
    .kb-toolbar { margin-top: 28px; }

    /* Search */
    .kb-search-wrap { max-width: 560px; }
    .kb-search-icon {
      position: absolute;
      left: 14px;
      top: 50%;
      transform: translateY(-50%);
      color: #9ca3af;
      pointer-events: none;
    }
    .kb-search-input {
      width: 100%;
      height: 44px;
      border: 1px solid #e5e7eb;
      border-radius: 10px;
      padding: 0 42px 0 40px;
      background: #fff;
      font-size: 0.875rem;
      color: #1f2937;
      transition: border-color 0.15s ease, box-shadow 0.15s ease;
      font-family: inherit;
    }
    .kb-search-input::placeholder { color: #9ca3af; }
    .kb-search-input:focus {
      outline: none;
      border-color: #4f46e5;
      box-shadow: 0 0 0 3px rgba(79, 70, 229, 0.1);
    }
    .kb-search-clear {
      position: absolute;
      right: 8px;
      top: 50%;
      transform: translateY(-50%);
      width: 28px;
      height: 28px;
      border: 0;
      background: transparent;
      color: #6b7280;
      border-radius: 6px;
      display: inline-flex;
      align-items: center;
      justify-content: center;
      cursor: pointer;
      transition: background 0.15s ease, color 0.15s ease;
    }
    .kb-search-clear:hover { background: #f3f4f6; color: #1f2937; }
    .kb-result-line {
      font-size: 0.8125rem;
      color: #6b7280;
    }
    .kb-link-btn {
      background: transparent;
      border: 0;
      padding: 0;
      color: #4f46e5;
      cursor: pointer;
      font: inherit;
    }
    .kb-link-btn:hover { color: #4338ca; text-decoration: underline; }

    /* Buttons */
    .kb-btn {
      display: inline-flex;
      align-items: center;
      font-size: 0.8125rem;
      font-weight: 600;
      letter-spacing: 0.005em;
      padding: 8px 14px;
      border-radius: 8px;
      border: 1px solid transparent;
      line-height: 1;
      cursor: pointer;
      font-family: inherit;
      text-decoration: none;
      transition: transform 0.15s ease, box-shadow 0.2s ease, background 0.2s ease, border-color 0.15s ease, color 0.15s ease;
    }
    .kb-btn:focus-visible {
      outline: none;
      box-shadow: 0 0 0 3px rgba(79, 70, 229, 0.2);
    }
    .kb-btn--ghost {
      background: #fff;
      border-color: #e5e7eb;
      color: #1f2937;
      box-shadow: 0 1px 2px rgba(17, 24, 39, 0.04);
    }
    .kb-btn--ghost:hover {
      background: #fafafa;
      border-color: #d1d5db;
      color: #1f2937;
      transform: translateY(-1px);
      text-decoration: none;
    }
    .kb-btn--primary {
      background: linear-gradient(180deg, #6366f1 0%, #4f46e5 100%);
      color: #fff;
      border-color: rgba(255, 255, 255, 0.04);
      box-shadow:
        0 1px 0 rgba(255, 255, 255, 0.18) inset,
        0 1px 2px rgba(79, 70, 229, 0.22),
        0 4px 10px rgba(79, 70, 229, 0.2);
    }
    .kb-btn--primary:hover {
      background: linear-gradient(180deg, #4f46e5 0%, #4338ca 100%);
      color: #fff;
      transform: translateY(-1px);
      text-decoration: none;
      box-shadow:
        0 1px 0 rgba(255, 255, 255, 0.18) inset,
        0 2px 4px rgba(67, 56, 202, 0.28),
        0 8px 18px rgba(79, 70, 229, 0.3);
    }

    .kb-icon-btn {
      width: 32px;
      height: 32px;
      border: 1px solid #e5e7eb;
      background: #fff;
      color: #4b5563;
      border-radius: 8px;
      display: inline-flex;
      align-items: center;
      justify-content: center;
      cursor: pointer;
      box-shadow: 0 1px 2px rgba(17, 24, 39, 0.04);
      transition: transform 0.15s ease, background 0.2s ease, border-color 0.15s ease, color 0.15s ease, box-shadow 0.2s ease;
    }
    .kb-icon-btn:hover {
      background: #fafafa;
      border-color: #d1d5db;
      color: #1f2937;
      transform: translateY(-1px);
    }
    .kb-icon-btn--danger:hover {
      color: #b91c1c;
      background: #fef2f2;
      border-color: #fecaca;
    }
    .kb-icon-btn--active {
      background: linear-gradient(180deg, #6366f1 0%, #4f46e5 100%);
      border-color: rgba(255, 255, 255, 0.04);
      color: #fff;
    }
    .kb-icon-btn--active:hover {
      background: linear-gradient(180deg, #4f46e5 0%, #4338ca 100%);
      color: #fff;
    }

    /* Section */
    .kb-section { padding: 32px 16px 64px; }
    .kb-section-title-left {
      font-size: 1.0625rem;
      font-weight: 600;
      color: #1f2937;
      letter-spacing: -0.005em;
      margin: 0 0 18px;
    }

    /* Article card */
    .kb-article-card {
      display: block;
      background: #fff;
      border: 1px solid #ececec;
      border-radius: 12px;
      padding: 14px 16px;
      cursor: pointer;
      transition: border-color 0.15s ease, box-shadow 0.2s ease, transform 0.15s ease;
    }
    .kb-article-card:hover {
      border-color: #c7d2fe;
      box-shadow: 0 1px 2px rgba(17, 24, 39, 0.04), 0 6px 14px rgba(79, 70, 229, 0.08);
      transform: translateY(-1px);
    }
    .kb-article-card-title {
      font-size: 0.9375rem;
      font-weight: 600;
      color: #1f2937;
      letter-spacing: -0.005em;
    }
    .kb-article-card-desc {
      font-size: 0.8125rem;
      color: #6b7280;
      line-height: 1.5;
    }
    .kb-article-card-meta {
      font-size: 0.6875rem;
      font-weight: 500;
      color: #9ca3af;
      letter-spacing: 0.02em;
    }

    /* Empty */
    .kb-empty {
      border: 1px dashed #e5e5e5;
      border-radius: 12px;
      background: #fafafa;
      padding: 40px 24px;
    }
    .kb-empty-icon {
      width: 44px;
      height: 44px;
      border-radius: 50%;
      background: #fff;
      border: 1px solid #ececec;
      color: #9ca3af;
    }
    .kb-empty-title {
      font-size: 0.9375rem;
      font-weight: 600;
      color: #1f2937;
      margin: 0 0 4px;
    }
    .kb-empty-text {
      font-size: 0.8125rem;
      color: #6b7280;
    }
  `}</style>
);

export default CategoryArticles;
